type Listener<T> = (payload: T) => void;

/**
 * A minimal typed event emitter.
 *
 * WalletFront broadcast auth/rate-limit events through `window.dispatchEvent`
 * with untyped `CustomEvent` details, which meant every consumer cast blindly
 * and nothing was checked at compile time. Same pattern, typed, and not bolted
 * onto the global object.
 */
export class Emitter<Events> {
  // `never` as the payload type is what makes the heterogeneous map assignable;
  // the public `on`/`emit` signatures restore per-event typing.
  private readonly listeners = new Map<keyof Events, Set<Listener<never>>>();

  on<K extends keyof Events>(event: K, listener: Listener<Events[K]>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  }

  emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    // Copy: a listener may unsubscribe itself while we iterate.
    for (const listener of [...set]) {
      (listener as Listener<Events[K]>)(payload);
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}
